/**
 * Config API hooks for Studio.
 * Provides React hooks for interacting with the config management API.
 */

import { useState, useCallback } from "react";

import type {
  ConfigType,
  ConfigDiffSummary,
  ConfigValidationResult,
  VersionHistoryEntry,
  VersionHistoryResponse,
  ApiResponse,
  ApiError,
} from "../components/diff/types";
import type { PromotionResult, RollbackResult } from "../components/promotion/types";

// ============================================================================
// Types
// ============================================================================

/** Config metadata from API */
export interface ConfigMetadata {
  readonly configType: ConfigType;
  readonly version: number;
  readonly status: "draft" | "live" | "archived";
  readonly checksum: string;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly changeSummary?: string;
}

/** Config response from API */
export interface ConfigResponse {
  readonly data: unknown;
  readonly metadata: ConfigMetadata;
}

/** Diff response from API */
export interface DiffResponse {
  readonly verticalKey: string;
  readonly configType: ConfigType;
  readonly hasChanges: boolean;
  readonly totalChanges: number;
  readonly addedFields: readonly string[];
  readonly removedFields: readonly string[];
  readonly changedFields: readonly string[];
  readonly summary: string;
  readonly liveVersion: number | null;
  readonly draftVersion: number;
}

/** Validation response from API */
export interface ValidationResponse {
  readonly verticalKey: string;
  readonly configType: ConfigType;
  readonly schemaValid: boolean;
  readonly schemaErrors: ReadonlyArray<{
    readonly path: string;
    readonly message: string;
    readonly code: string;
    readonly expected?: unknown;
    readonly actual?: unknown;
  }>;
  readonly referencesValid: boolean;
  readonly referenceErrors: ReadonlyArray<{
    readonly path: string;
    readonly message: string;
    readonly code: string;
    readonly sourceConfig?: string;
    readonly referencedConfig?: string;
    readonly reference?: string;
  }>;
  readonly canPromote: boolean;
  readonly validatedAt: string;
}

/** API client configuration */
export interface ApiClientConfig {
  readonly baseUrl: string;
  readonly getAuthToken: () => string | null;
}

// ============================================================================
// API Client
// ============================================================================

/**
 * Create an API client for config management.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createConfigApiClient(config: ApiClientConfig) {
  const { baseUrl, getAuthToken } = config;

  const fetchWithAuth = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      throw new Error(error.error?.message || `API error: ${response.status}`);
    }

    return data as T;
  };

  return {
    /**
     * Get live or versioned config.
     */
    getConfig: async (
      verticalKey: string,
      configType: ConfigType,
      version?: number
    ): Promise<ConfigResponse> => {
      const query = version !== undefined ? `?version=${version}` : "";
      const response = await fetchWithAuth<ApiResponse<unknown> & { metadata: ConfigMetadata }>(
        `/api/v1/configs/${verticalKey}/${configType}${query}`
      );
      return {
        data: response.data,
        metadata: response.metadata,
      };
    },

    /**
     * Get draft config.
     */
    getDraft: async (verticalKey: string, configType: ConfigType): Promise<ConfigResponse> => {
      const response = await fetchWithAuth<ApiResponse<unknown> & { metadata: ConfigMetadata }>(
        `/api/v1/configs/${verticalKey}/${configType}/draft`
      );
      return {
        data: response.data,
        metadata: response.metadata,
      };
    },

    /**
     * Save draft config.
     */
    saveDraft: async (
      verticalKey: string,
      configType: ConfigType,
      data: unknown,
      changeSummary?: string
    ): Promise<ConfigMetadata> => {
      const response = await fetchWithAuth<ApiResponse<ConfigMetadata>>(
        `/api/v1/configs/${verticalKey}/${configType}/draft`,
        {
          method: "PUT",
          body: JSON.stringify({ data, changeSummary }),
        }
      );
      return response.data;
    },

    /**
     * Delete draft config.
     */
    deleteDraft: async (verticalKey: string, configType: ConfigType): Promise<void> => {
      await fetchWithAuth(`/api/v1/configs/${verticalKey}/${configType}/draft`, {
        method: "DELETE",
      });
    },

    /**
     * Validate draft config.
     */
    validateDraft: async (
      verticalKey: string,
      configType: ConfigType
    ): Promise<ConfigValidationResult> => {
      const response = await fetchWithAuth<ApiResponse<ValidationResponse>>(
        `/api/v1/configs/${verticalKey}/${configType}/validate`,
        { method: "POST" }
      );
      const data = response.data;
      return {
        schemaValid: data.schemaValid,
        schemaErrors: data.schemaErrors.map((e) => ({
          path: e.path,
          message: e.message,
          code: e.code,
          severity: "error" as const,
        })),
        referencesValid: data.referencesValid,
        referenceErrors: data.referenceErrors.map((e) => ({
          path: e.path,
          message: e.message,
          code: e.code,
          severity: "warning" as const,
        })),
        canPromote: data.canPromote,
        validatedAt: data.validatedAt,
      };
    },

    /**
     * Get diff between draft and live.
     */
    getDiff: async (verticalKey: string, configType: ConfigType): Promise<ConfigDiffSummary> => {
      const response = await fetchWithAuth<ApiResponse<DiffResponse>>(
        `/api/v1/configs/${verticalKey}/${configType}/diff`
      );
      const data = response.data;
      return {
        hasChanges: data.hasChanges,
        totalChanges: data.totalChanges,
        addedFields: [...data.addedFields],
        removedFields: [...data.removedFields],
        changedFields: [...data.changedFields],
        summary: data.summary,
      };
    },

    /**
     * Promote draft to live.
     */
    promote: async (
      verticalKey: string,
      configType: ConfigType,
      _changeSummary?: string
    ): Promise<PromotionResult> => {
      const response = await fetchWithAuth<ApiResponse<PromotionResult>>(
        `/api/v1/configs/${verticalKey}/${configType}/publish`,
        { method: "POST" }
      );
      return response.data;
    },

    /**
     * Get version history.
     */
    getHistory: async (
      verticalKey: string,
      configType: ConfigType,
      options?: { limit?: number; offset?: number; status?: string }
    ): Promise<VersionHistoryResponse> => {
      const params = new URLSearchParams();
      if (options?.limit !== undefined) params.set("limit", options.limit.toString());
      if (options?.offset !== undefined) params.set("offset", options.offset.toString());
      if (options?.status !== undefined) params.set("status", options.status);
      const query = params.toString() ? `?${params.toString()}` : "";
      return fetchWithAuth<VersionHistoryResponse>(
        `/api/v1/configs/${verticalKey}/${configType}/history${query}`
      );
    },

    /**
     * Rollback to a previous version.
     */
    rollback: async (
      verticalKey: string,
      configType: ConfigType,
      version: number,
      reason?: string
    ): Promise<RollbackResult> => {
      const response = await fetchWithAuth<ApiResponse<RollbackResult>>(
        `/api/v1/configs/${verticalKey}/${configType}/rollback`,
        {
          method: "POST",
          body: JSON.stringify({ version, reason }),
        }
      );
      return response.data;
    },
  };
}

export type ConfigApiClient = ReturnType<typeof createConfigApiClient>;

// ============================================================================
// React Hooks
// ============================================================================

/** State for async operations */
interface AsyncState<T> {
  readonly data: T | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

/**
 * Hook for fetching and managing config diff.
 */
export function useConfigDiff(
  client: ConfigApiClient,
  verticalKey: string,
  configType: ConfigType
): {
  diff: ConfigDiffSummary | null;
  liveContent: string | null;
  draftContent: string | null;
  liveVersion: number | null;
  draftVersion: number | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<
    AsyncState<{
      diff: ConfigDiffSummary;
      liveContent: string;
      draftContent: string;
      liveVersion: number | null;
      draftVersion: number;
    }>
  >({
    data: null,
    isLoading: false,
    error: null,
  });

  const refresh = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Fetch draft and diff in parallel
      const [diff, draft] = await Promise.all([
        client.getDiff(verticalKey, configType),
        client.getDraft(verticalKey, configType),
      ]);

      // Try to fetch live config (may not exist)
      let liveContent = "";
      let liveVersion: number | null = null;
      try {
        const live = await client.getConfig(verticalKey, configType);
        liveContent = JSON.stringify(live.data, null, 2);
        liveVersion = live.metadata.version;
      } catch {
        // Live version may not exist yet
      }

      setState({
        data: {
          diff,
          liveContent,
          draftContent: JSON.stringify(draft.data, null, 2),
          liveVersion,
          draftVersion: draft.metadata.version,
        },
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load diff",
      }));
    }
  }, [client, verticalKey, configType]);

  return {
    diff: state.data?.diff ?? null,
    liveContent: state.data?.liveContent ?? null,
    draftContent: state.data?.draftContent ?? null,
    liveVersion: state.data?.liveVersion ?? null,
    draftVersion: state.data?.draftVersion ?? null,
    isLoading: state.isLoading,
    error: state.error,
    refresh,
  };
}

/**
 * Hook for config validation.
 */
export function useConfigValidation(
  client: ConfigApiClient,
  verticalKey: string,
  configType: ConfigType
): {
  validation: ConfigValidationResult | null;
  isValidating: boolean;
  error: string | null;
  validate: () => Promise<ConfigValidationResult>;
} {
  const [state, setState] = useState<AsyncState<ConfigValidationResult>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const validate = useCallback(async (): Promise<ConfigValidationResult> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await client.validateDraft(verticalKey, configType);
      setState({ data: result, isLoading: false, error: null });
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Validation failed";
      setState((prev) => ({ ...prev, isLoading: false, error: errorMsg }));
      throw err;
    }
  }, [client, verticalKey, configType]);

  return {
    validation: state.data,
    isValidating: state.isLoading,
    error: state.error,
    validate,
  };
}

/**
 * Hook for version history.
 */
export function useVersionHistory(
  client: ConfigApiClient,
  verticalKey: string,
  configType: ConfigType
): {
  entries: readonly VersionHistoryEntry[];
  pagination: VersionHistoryResponse["pagination"] | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
} {
  const [state, setState] = useState<AsyncState<VersionHistoryResponse>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const refresh = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await client.getHistory(verticalKey, configType, { limit: 20 });
      setState({ data: result, isLoading: false, error: null });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load history",
      }));
    }
  }, [client, verticalKey, configType]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!state.data?.pagination.hasMore) return;

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await client.getHistory(verticalKey, configType, {
        limit: 20,
        offset: state.data.pagination.offset + state.data.pagination.limit,
      });
      setState((prev) => ({
        data: prev.data
          ? {
              data: [...prev.data.data, ...result.data],
              pagination: result.pagination,
            }
          : result,
        isLoading: false,
        error: null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load more",
      }));
    }
  }, [client, verticalKey, configType, state.data]);

  return {
    entries: state.data?.data ?? [],
    pagination: state.data?.pagination ?? null,
    isLoading: state.isLoading,
    error: state.error,
    refresh,
    loadMore,
  };
}

/**
 * Hook for promotion workflow.
 */
export function usePromotion(
  client: ConfigApiClient,
  verticalKey: string,
  configType: ConfigType
): {
  isPromoting: boolean;
  error: string | null;
  promote: (changeSummary: string) => Promise<PromotionResult>;
} {
  const [state, setState] = useState<{
    isPromoting: boolean;
    error: string | null;
  }>({
    isPromoting: false,
    error: null,
  });

  const promote = useCallback(
    async (changeSummary: string): Promise<PromotionResult> => {
      setState({ isPromoting: true, error: null });

      try {
        const result = await client.promote(verticalKey, configType, changeSummary);
        setState({ isPromoting: false, error: null });
        return result;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Promotion failed";
        setState({ isPromoting: false, error: errorMsg });
        throw err;
      }
    },
    [client, verticalKey, configType]
  );

  return {
    isPromoting: state.isPromoting,
    error: state.error,
    promote,
  };
}

/**
 * Hook for rollback workflow.
 */
export function useRollback(
  client: ConfigApiClient,
  verticalKey: string,
  configType: ConfigType
): {
  isRollingBack: boolean;
  error: string | null;
  rollback: (version: number, reason: string) => Promise<RollbackResult>;
} {
  const [state, setState] = useState<{
    isRollingBack: boolean;
    error: string | null;
  }>({
    isRollingBack: false,
    error: null,
  });

  const rollback = useCallback(
    async (version: number, reason: string): Promise<RollbackResult> => {
      setState({ isRollingBack: true, error: null });

      try {
        const result = await client.rollback(verticalKey, configType, version, reason);
        setState({ isRollingBack: false, error: null });
        return result;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Rollback failed";
        setState({ isRollingBack: false, error: errorMsg });
        throw err;
      }
    },
    [client, verticalKey, configType]
  );

  return {
    isRollingBack: state.isRollingBack,
    error: state.error,
    rollback,
  };
}
